import React, { useMemo } from 'react';

interface DebugGridOverlayProps {
  width: number; // in mm
  height: number; // in mm
  majorStep?: number; // default 50mm
  minorStep?: number; // default 10mm
}

export function DebugGridOverlay({ 
  width, 
  height, 
  majorStep = 50, 
  minorStep = 10 
}: DebugGridOverlayProps) {
  
  const grid = useMemo(() => {
      const elements = [];
      
      // Vertical lines
      for (let x = 0; x <= width; x += minorStep) {
        const isMajor = x % majorStep === 0;
        elements.push(
          <line 
            key={`v-${x}`} 
            x1={x} y1={0} 
            x2={x} y2={height} 
            stroke={isMajor ? "rgba(220, 38, 38, 0.5)" : "rgba(220, 38, 38, 0.15)"} // red-600
            strokeWidth={isMajor ? 0.5 : 0.2} 
            vectorEffect="non-scaling-stroke"
          />
        );
        
        if (isMajor) {
           // X-axis labels at top
           elements.push(
            <text 
              key={`tx-${x}`} 
              x={x + 1} 
              y={5} 
              fill="#dc2626" 
              fontSize="4"
              fontFamily="monospace"
              fontWeight="bold"
            >
              {x}
            </text>
           );
        }
      }

      // Horizontal lines
      for (let y = 0; y <= height; y += minorStep) {
        const isMajor = y % majorStep === 0;
        elements.push(
          <line 
            key={`h-${y}`} 
            x1={0} y1={y} 
            x2={width} y2={y} 
            stroke={isMajor ? "rgba(220, 38, 38, 0.5)" : "rgba(220, 38, 38, 0.15)"} 
            strokeWidth={isMajor ? 0.5 : 0.2} 
            vectorEffect="non-scaling-stroke"
          />
        );
        
        if (isMajor) {
           // Y-axis labels at left
           elements.push(
            <text 
              key={`ty-${y}`} 
              x={2} 
              y={y - 2} 
              fill="#dc2626" 
              fontSize="4"
              fontFamily="monospace"
              fontWeight="bold"
            >
              {y}
            </text>
           );
        }
      }
      
      // Intersection coordinates
      for (let x = 0; x <= width; x += majorStep) {
        for (let y = 0; y <= height; y += majorStep) {
            if (x === 0 && y === 0) continue; // Skip 0,0 as it overlaps with axis labels
            elements.push(
                <text
                    key={`coord-${x}-${y}`}
                    x={x + 1}
                    y={y + 4}
                    fill="#dc2626"
                    fontSize="2.5"
                    fontFamily="monospace"
                    opacity="0.7"
                >
                    {x},{y}
                </text>
            );
            
            // Small cross marker
            elements.push(
                <line 
                    key={`cross-1-${x}-${y}`}
                    x1={x - 1} y1={y} x2={x + 1} y2={y}
                    stroke="#dc2626" strokeWidth="0.5"
                />
            );
            elements.push(
                <line 
                    key={`cross-2-${x}-${y}`}
                    x1={x} y1={y - 1} x2={x} y2={y + 1}
                    stroke="#dc2626" strokeWidth="0.5"
                />
            );
        }
      }

      return elements;
  }, [width, height, majorStep, minorStep]);

  return (
    <svg 
      width="100%" 
      height="100%" 
      viewBox={`0 0 ${width} ${height}`}
      className="absolute inset-0 pointer-events-none z-[9999]"
      style={{ mixBlendMode: 'multiply' }}
    >
      {grid}
      
      {/* Outer Border */}
      <rect 
        x={0} y={0} 
        width={width} height={height} 
        fill="none" 
        stroke="#dc2626" 
        strokeWidth="1" 
      />
    </svg>
  );
}
